import { Meeting } from '../types/api';

// Type definitions
export interface MeetingWithLayout extends Meeting {
  column: number;
  columnCount: number;
  overlaps: number[];
}

export interface PositionedMeeting extends MeetingWithLayout {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface CollisionGroup {
  meetings: MeetingWithLayout[];
  columnCount: number;
}

// Configuration constants
export const LAYOUT_CONFIG = {
  PIXELS_PER_HOUR_WEEK: 100,
  PIXELS_PER_HOUR_DAY: 120,
  MIN_EVENT_HEIGHT: 20,
  EVENT_PADDING: 2,
};

/**
 * Detect overlapping meetings
 * Returns array with overlap information tracked
 */
export function detectOverlaps(meetings: Meeting[]): MeetingWithLayout[] {
  // Sort meetings by start time, then by duration (longer first)
  const sorted = [...meetings].sort((a, b) => {
    if (a.start !== b.start) return a.start - b.start;
    return (b.end - b.start) - (a.end - a.start); // Longer events first
  });

  const withOverlaps: MeetingWithLayout[] = sorted.map(meeting => ({
    ...meeting,
    column: 0,
    columnCount: 1,
    overlaps: []
  }));

  // For each meeting, find all meetings that overlap with it
  for (let i = 0; i < withOverlaps.length; i++) {
    for (let j = i + 1; j < withOverlaps.length; j++) {
      const meetingA = withOverlaps[i];
      const meetingB = withOverlaps[j];

      // Check if they overlap: A.end > B.start AND A.start < B.end
      if (doMeetingsOverlap(meetingA, meetingB)) {
        withOverlaps[i].overlaps.push(meetingB.id);
        withOverlaps[j].overlaps.push(meetingA.id);
      }
    }
  }

  return withOverlaps;
}

/**
 * Check if two meetings overlap
 */
function doMeetingsOverlap(a: Meeting, b: Meeting): boolean {
  return a.end > b.start && a.start < b.end;
}

/**
 * Get all meetings that are connected through overlaps (transitive closure)
 */
function getCollisionGroup(
  start: MeetingWithLayout,
  allMeetings: MeetingWithLayout[]
): MeetingWithLayout[] {
  const group = new Set<number>([start.id]);
  const queue = [start];

  while (queue.length > 0) {
    const current = queue.shift()!;

    for (const overlapId of current.overlaps) {
      if (!group.has(overlapId)) {
        group.add(overlapId);
        const overlapping = allMeetings.find(m => m.id === overlapId)!;
        queue.push(overlapping);
      }
    }
  }

  return allMeetings.filter(m => group.has(m.id));
}

/**
 * Assign columns to a group of meetings using a greedy algorithm
 */
function assignColumnsToGroup(group: MeetingWithLayout[]): void {
  // Sort by start time (already done from detectOverlaps)
  const columns: MeetingWithLayout[][] = [];

  for (const meeting of group) {
    // Find the first column where this meeting doesn't overlap with the last meeting
    let assignedColumn = -1;

    for (let col = 0; col < columns.length; col++) {
      const lastInColumn = columns[col][columns[col].length - 1];

      // If this meeting starts after the last meeting in this column ends
      if (meeting.start >= lastInColumn.end) {
        assignedColumn = col;
        break;
      }
    }

    // If no suitable column found, create a new one
    if (assignedColumn === -1) {
      assignedColumn = columns.length;
      columns.push([]);
    }

    meeting.column = assignedColumn;
    columns[assignedColumn].push(meeting);
  }

  // Set columnCount for all meetings in the group
  const maxColumns = columns.length;
  group.forEach(m => (m.columnCount = maxColumns));
}

/**
 * Assign columns to all meetings, grouping by collision groups
 */
export function assignColumns(meetingsWithOverlaps: MeetingWithLayout[]): CollisionGroup[] {
  const collisionGroups: CollisionGroup[] = [];
  const processed = new Set<number>();

  // Group meetings into collision groups (transitive overlap)
  for (const meeting of meetingsWithOverlaps) {
    if (processed.has(meeting.id)) continue;

    const group = getCollisionGroup(meeting, meetingsWithOverlaps);
    assignColumnsToGroup(group);

    collisionGroups.push({
      meetings: group,
      columnCount: Math.max(...group.map(m => m.column + 1))
    });

    group.forEach(m => processed.add(m.id));
  }

  return collisionGroups;
}

/**
 * Calculate pixel position and dimensions for a meeting
 */
export function calculatePosition(
  meeting: MeetingWithLayout,
  dayStartHour: number,
  pixelsPerHour: number
): { top: number; height: number; left: number; width: number } {
  // Calculate TOP position (pixels from start of displayed time range)
  const meetingStartDate = new Date(meeting.start);
  const meetingHour = meetingStartDate.getHours();
  const meetingMinute = meetingStartDate.getMinutes();
  const minutesFromDayStart = (meetingHour - dayStartHour) * 60 + meetingMinute;
  const top = (minutesFromDayStart / 60) * pixelsPerHour;

  // Calculate HEIGHT (based on duration)
  const durationMs = meeting.end - meeting.start;
  const durationMinutes = durationMs / (1000 * 60);
  const height = Math.max(
    LAYOUT_CONFIG.MIN_EVENT_HEIGHT,
    (durationMinutes / 60) * pixelsPerHour - LAYOUT_CONFIG.EVENT_PADDING
  );

  // Calculate WIDTH (percentage based on column count)
  const widthPercent = 100 / meeting.columnCount;

  // Calculate LEFT (percentage based on column index)
  const leftPercent = meeting.column * widthPercent;

  return {
    top: Math.round(top),
    height: Math.round(height),
    left: leftPercent,
    width: widthPercent
  };
}

/**
 * Get all meetings for a specific day with calculated layout positions
 */
export function getMeetingsForDayWithLayout(
  date: Date,
  allMeetings: Meeting[],
  dayStartHour: number = 8,
  pixelsPerHour: number = LAYOUT_CONFIG.PIXELS_PER_HOUR_WEEK
): PositionedMeeting[] {
  // Get all meetings for this day
  const dayStart = new Date(date);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(date);
  dayEnd.setHours(23, 59, 59, 999);

  const dayMeetings = allMeetings.filter(
    meeting => meeting.start >= dayStart.getTime() && meeting.start <= dayEnd.getTime()
  );

  // If no meetings, return empty array
  if (dayMeetings.length === 0) {
    return [];
  }

  // Apply full layout algorithm
  const withOverlaps = detectOverlaps(dayMeetings);
  const groups = assignColumns(withOverlaps);

  // Calculate positions
  const positioned: PositionedMeeting[] = [];

  for (const group of groups) {
    for (const meeting of group.meetings) {
      const position = calculatePosition(meeting, dayStartHour, pixelsPerHour);
      positioned.push({
        ...meeting,
        ...position
      });
    }
  }

  return positioned;
}

/**
 * Truncate a meeting to a specific day's boundaries
 */
export function truncateMeetingToDay(meeting: Meeting, date: Date): Meeting {
  const dayStart = new Date(date);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(date);
  dayEnd.setHours(23, 59, 59, 999);

  return {
    ...meeting,
    start: Math.max(meeting.start, dayStart.getTime()),
    end: Math.min(meeting.end, dayEnd.getTime())
  };
}
